import { useNavigate, useSearch } from '@tanstack/react-router';
import { BarChart3, Pause, PencilLine, Play, Plus } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { formatMoney, HIRE_KIND_LABELS, jevBudgetRequestLineOf } from '@nest-recruit/shared';
import { isCurrency } from '../requisition-form-values';
import type {
  HmOpeningCard,
  RequisitionAction,
  RequisitionDetail,
  RequisitionStatus,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Drawer } from '../../../components/Drawer';
import { SkeletonLines } from '../../../components/Skeleton';
import { APPROVAL_STEP_TONES, REQUISITION_STATUS_TONES } from '../../../components/status-tones';
import { ToneIcon, toneCardClass, toneClass } from '../../../components/Tone';
import {
  EmptyState,
  ForbiddenState,
  ServerErrorState,
} from '../../../components/system-states/SystemState';
import { errorMessage } from '../../../lib/api-client';
import { EMPTY, formatDateTime, formatIsoDate, formatRelative } from '../../../lib/format';
import { useModule } from '../../../lib/modules';
import { useCanView } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { SalaryBand } from '../../organisation/components/SalaryBand';
import { useActionTimer, useHmOpenings } from '../api/hm-api';
import { useRequisition, useRequisitionAction } from '../api/requisitions-api';
import { FunnelBar, WaitingOn } from '../components/HmBadges';
import { ReasonDialog } from '../components/ReasonDialog';
import {
  APPROVAL_STEP_LABELS,
  PriorityDot,
  RequisitionStatusChip,
  SlaFigure,
  requisitionTone,
} from '../components/RequisitionBadges';
import styles from './MyOpeningsPage.module.css';
import { AskHrSection } from '../../communication/components/AskHrSection';

/**
 * HM-01 — each of the manager's requisitions as a status card (E16): the stage
 * funnel mini-bar, "waiting on: you / HR / candidate", and what happens next. One
 * tap opens it, read-only apart from the moves §L25 gives its hiring manager.
 */

export interface MyOpeningsSearch {
  /** N-04 / N-07 land here with the opening open. */
  readonly open?: string;
}

/** A funnel before a job exists would be five zeros that mean nothing yet. */
const HAS_FUNNEL: readonly RequisitionStatus[] = ['approved', 'converted', 'on_hold', 'reopened'];

export function MyOpeningsPage(): ReactNode {
  const canView = useCanView('requisition');
  const search: MyOpeningsSearch = useSearch({ from: '/app-layout/my/openings' });
  const navigate = useNavigate();
  const openings = useHmOpenings();

  if (!canView) return <ForbiddenState resource="hiring requests" contact="Your admin" />;
  if (openings.isError) return <ServerErrorState onRetry={() => void openings.refetch()} />;

  const cards = openings.data ?? [];
  const waitingOnYou = cards.filter((card) => card.ballInCourt === 'you').length;

  function open(id: string | null): void {
    void navigate({ to: '/my/openings', search: id ? { open: id } : {} });
  }

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div className={styles.headText}>
          <h1 className={styles.title}>My openings</h1>
          <p className={styles.lead}>
            {cards.length === 0
              ? 'Nothing open yet.'
              : `${cards.length} open${waitingOnYou > 0 ? `, ${waitingOnYou} waiting on you` : ''}.`}
          </p>
        </div>
        <span className={styles.headActions}>
          {/* S29 (Q3): the manager's own departments on RPT-04 — the other half of accountability. */}
          <Button variant="outline" icon={BarChart3} onClick={() => void navigate({ to: '/reports/scoreboard', search: {} })}>
            Department scoreboard
          </Button>
          <Button variant="solid" icon={Plus} onClick={() => void navigate({ to: '/request-hire' })}>
            Request a hire
          </Button>
        </span>
      </header>

      {openings.isPending ? (
        <SkeletonLines count={6} />
      ) : cards.length === 0 ? (
        <EmptyState
          title="No openings yet"
          body="Every hire you request shows here, with whose move it is and how far candidates have got."
          action={null}
        />
      ) : (
        <ul className={styles.grid}>
          {cards.map((card) => (
            <li key={card.id}>
              <OpeningCard card={card} onOpen={() => open(card.id)} />
            </li>
          ))}
        </ul>
      )}

      {search.open && <OpeningDrawer requisitionId={search.open} onClose={() => open(null)} />}
    </div>
  );
}

function OpeningCard({ card, onOpen }: { readonly card: HmOpeningCard; readonly onOpen: () => void }): ReactNode {
  // Rule 13 (bug B9): with Time limits off the clock neither shows nor colours the card.
  const isSlaOn = useModule('sla');
  const tone = requisitionTone(card.status, isSlaOn ? card.sla : null);
  return (
    <button
      type="button"
      className={`${styles.card} ${toneCardClass(true)}`}
      data-tone={tone}
      data-yours={card.ballInCourt === 'you'}
      onClick={onOpen}
    >
      <span className={styles.top}>
        <ToneIcon tone={tone} />
        <span className={styles.ref}>{card.reference}</span>
        <RequisitionStatusChip status={card.status} />
        <PriorityDot priority={card.priority} />
      </span>
      <span className={styles.cardTitle}>{card.title}</span>
      <span className={styles.meta}>
        {card.departmentName} · {card.slotsRequested} {card.slotsRequested === 1 ? 'hire' : 'hires'}
      </span>
      <WaitingOn ball={card.ballInCourt} name={card.waitingOnName} />
      <span className={styles.next}>
        <span className={styles.nextLabel}>Next</span> {card.nextStep}
      </span>
      {HAS_FUNNEL.includes(card.status) && <FunnelBar funnel={card.funnel} />}
      {card.sla && <SlaFigure sla={card.sla} showLimit />}
    </button>
  );
}

// --- One opening, opened --------------------------------------------------------------

type Prompt = 'hold' | 'withdraw' | null;

export function OpeningDrawer({
  requisitionId,
  onClose,
}: {
  readonly requisitionId: string;
  readonly onClose: () => void;
}): ReactNode {
  const requisition = useRequisition(requisitionId);
  const detail = requisition.data ?? null;

  return (
    <Drawer
      isOpen
      onClose={onClose}
      title={detail ? detail.title : 'Opening'}
      {...(detail ? { meta: `${detail.reference} · ${detail.departmentName}` } : {})}
      {...(detail ? { footer: <OpeningActions detail={detail} /> } : {})}
    >
      {requisition.isError ? (
        requisition.error.status === 403 ? (
          <ForbiddenState resource="this requisition" contact="Your admin" isInset />
        ) : (
          <ServerErrorState onRetry={() => void requisition.refetch()} />
        )
      ) : detail ? (
        <OpeningDetail detail={detail} />
      ) : (
        <SkeletonLines count={8} />
      )}
    </Drawer>
  );
}

export function OpeningDetail({ detail }: { readonly detail: RequisitionDetail }): ReactNode {
  // Its heading and figure belong to Time limits: nothing of it shows while the part is off (rule 13, bug B9).
  const isSlaOn = useModule('sla');
  const round = detail.approvals.filter((step) => step.requisitionVersion === detail.currentVersion);

  return (
    <div className={styles.detail}>
      <div className={styles.detailTop}>
        <RequisitionStatusChip status={detail.status} />
        <PriorityDot priority={detail.priority} />
      </div>
      {detail.statusReason && (
        <p className={`${toneClass} ${styles.reason}`} data-tone={REQUISITION_STATUS_TONES[detail.status]}>
          “{detail.statusReason}”
        </p>
      )}

      <dl className={styles.facts}>
        <Fact label="Hires">{detail.slotsRequested}</Fact>
        <Fact label="Start">{rangeOf(detail.targetStartDate ? formatIsoDate(detail.targetStartDate) : null, detail.targetStartLatest ? formatIsoDate(detail.targetStartLatest) : null)}</Fact>
        <Fact label="Position">{detail.positionCode ?? 'New position'}</Fact>
        <Fact label="Type">
          {detail.hireKind === 'replacement'
            ? `${HIRE_KIND_LABELS.replacement}${detail.replacesName ? `: ${detail.replacesName}` : ''}`
            : detail.hireKind === 'additional'
              ? HIRE_KIND_LABELS.additional
              : detail.positionId === null
                ? 'New position'
                : EMPTY}
        </Fact>
        <Fact label="Budget">{rangeOf(moneyOf(detail.budgetAmountMinor, detail.budgetCurrency), moneyOf(detail.budgetMaxMinor, detail.budgetCurrency))}</Fact>
        {/* §L23: the band is masked for a hiring manager — "—", never left out (ADR 0012). */}
        <Fact label="Salary band">{detail.position ? <SalaryBand position={detail.position} /> : EMPTY}</Fact>
        {/* ADR 0070: the requester sees what Jev budget was asked for and what the approvers settled on, on his own opening. */}
        <Fact label="Jev budget">{jevBudgetRequestLineOf(detail.jevBudgetRequestedMicroUsd, detail.jevBudgetApprovedMicroUsd) ?? EMPTY}</Fact>
        <Fact label="Recruiter">{detail.recruiterName ?? EMPTY}</Fact>
        <Fact label="Raised">{formatRelative(detail.createdAt)}</Fact>
        <Fact label="Approved">{detail.approvedAt ? formatDateTime(detail.approvedAt) : EMPTY}</Fact>
      </dl>

      {detail.timeToFill && isSlaOn && (
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Time to fill</h3>
          <SlaFigure sla={detail.timeToFill} showLimit />
        </section>
      )}

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Why it's needed</h3>
        <p className={styles.body}>{detail.justification ?? EMPTY}</p>
      </section>

      <AskHrSection requisitionId={detail.id} sectionClass={styles.section} titleClass={styles.sectionTitle} />

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Approvals</h3>
        {round.length === 0 ? (
          <p className={styles.muted}>
            {detail.status === 'draft' ? 'Not submitted yet.' : 'No approval chain — approved on submission.'}
          </p>
        ) : (
          <ol className={styles.trail}>
            {round.map((step) => (
              <li key={step.id} className={`${toneClass} ${styles.step}`} data-tone={APPROVAL_STEP_TONES[step.status]}>
                <span className={styles.stepName}>
                  {step.name} · {step.approverName}
                </span>
                <span className={styles.muted}>
                  {step.status === 'pending' && step.activatedAt ? 'Waiting' : APPROVAL_STEP_LABELS[step.status]}
                  {step.decidedAt ? ` · ${formatDateTime(step.decidedAt)}` : ''}
                  {step.actedByName && step.actedByName !== step.approverName ? ` · by ${step.actedByName}` : ''}
                </span>
                {step.decisionReason && <span className={styles.body}>“{step.decisionReason}”</span>}
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}

function Fact({ label, children }: { readonly label: string; readonly children: ReactNode }): ReactNode {
  return (
    <div className={styles.fact}>
      <dt className={styles.factLabel}>{label}</dt>
      <dd className={styles.factValue}>{children}</dd>
    </div>
  );
}

/** The moves §L25 gives a hiring manager on their own opening; the server re-checks each. */
function OpeningActions({ detail }: { readonly detail: RequisitionDetail }): ReactNode {
  const navigate = useNavigate();
  const act = useRequisitionAction();
  const timer = useActionTimer();
  const showToast = useToasts((state) => state.show);
  const [prompt, setPrompt] = useState<Prompt>(null);

  async function run(action: 'hold' | 'resume' | 'cancel', reason?: string): Promise<void> {
    try {
      await act.mutateAsync({ id: detail.id, action, version: detail.version, ...(reason ? { reason } : {}) });
      timer.finish(action === 'cancel' ? 'withdraw' : action, detail.id);
      setPrompt(null);
      showToast({ tone: 'success', message: `${detail.reference} ${DONE[action]}` });
    } catch (cause) {
      showToast({ tone: 'danger', message: 'That was not saved', detail: errorMessage(cause) });
    }
  }

  const can = (action: RequisitionAction): boolean => detail.actions.includes(action);
  if (!DRAWER_ACTIONS.some(can)) return null;

  return (
    <div className={styles.drawerActions}>
      {can('edit') && (
        <Button
          variant="solid"
          icon={PencilLine}
          onClick={() => void navigate({ to: '/request-hire/$requisitionId', params: { requisitionId: detail.id } })}
        >
          Edit and submit
        </Button>
      )}
      {can('hold') && (
        <Button variant="outline" icon={Pause} onClick={() => setPrompt('hold')}>
          Put on hold
        </Button>
      )}
      {can('resume') && (
        <Button variant="solid" icon={Play} isPending={act.isPending} onClick={() => void run('resume')}>
          Resume
        </Button>
      )}
      {can('cancel') && (
        <Button variant="ghost" onClick={() => setPrompt('withdraw')}>
          Withdraw
        </Button>
      )}

      {prompt === 'hold' && (
        <ReasonDialog
          title={`Put ${detail.reference} on hold?`}
          description="Its clock pauses until you resume it. HR and the recruiter are told."
          label="Reason"
          confirmLabel="Put on hold"
          isPending={act.isPending}
          onConfirm={(reason) => void run('hold', reason)}
          onClose={() => setPrompt(null)}
        />
      )}
      {prompt === 'withdraw' && (
        <ReasonDialog
          title={`Withdraw ${detail.reference}?`}
          description="It is closed for good. You can raise a new request at any time."
          label="Why (optional)"
          confirmLabel="Withdraw"
          isDanger
          isReasonOptional
          isPending={act.isPending}
          onConfirm={(reason) => void run('cancel', reason)}
          onClose={() => setPrompt(null)}
        />
      )}
    </div>
  );
}

const DRAWER_ACTIONS: readonly RequisitionAction[] = ['edit', 'hold', 'resume', 'cancel'];

const DONE: Readonly<Record<'hold' | 'resume' | 'cancel', string>> = {
  hold: 'put on hold',
  resume: 'resumed',
  cancel: 'withdrawn',
};

/** Workflow review 2026-10-29: the start and the budget are ranges. */
function rangeOf(from: string | null, to: string | null): string {
  if (from && to && from !== to) return `${from} – ${to}`;
  return from ?? to ?? EMPTY;
}

function moneyOf(minor: string | null, currency: string | null): string | null {
  if (minor === null || !isCurrency(currency)) return null;
  return formatMoney({ amountMinor: BigInt(minor), currency });
}
